// frontend/src/pages/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from '../routing';
import { useAuth } from '../context/AuthContext';
import { errorText } from '../utils/errors';
import toast from 'react-hot-toast';
import { Sparkles } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import AuthShell from '../components/AuthShell';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';


export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(t('auth.welcomeBackToast'));
      navigate(user.role === 'admin' ? '/admin' : '/browse');
    } catch (err) {
      // The server answers a wrong email-or-password with the English sentence
      // 'Invalid credentials', which this page's own helper preferred over the
      // translation — so t('auth.invalidCredentials') was unreachable and a
      // Greek customer with a typo got an English refusal. The 401 IS that case;
      // anything else (a validation body, an outage) keeps the server's words.
      toast.error(err?.status === 401
        ? t('auth.invalidCredentials')
        : errorText(err, t('auth.invalidCredentials')));
    } finally {
      setLoading(false);
    }
  };

  return <AuthLayout title={t('auth.welcomeBack')} subtitle={t('auth.signInSubtitle')}>
    <form onSubmit={handleSubmit}>
      <Input label={t('auth.email')} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
      <Input label={t('auth.password')} type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required />
      <div style={{ textAlign: 'right', marginTop: -12, marginBottom: 20 }}>
        <Link to="/forgot-password" style={{ color: theme.grey400, fontSize: 13, textDecoration: 'none' }}>{t('auth.forgotPassword')}</Link>
      </div>
      <SubmitBtn loading={loading} loadingLabel={t('auth.loading')}>{t('auth.signIn')}</SubmitBtn>
    </form>
    <p style={{ textAlign: 'center', color: theme.grey400, fontSize: 14, marginTop: 24 }}>
      {t('auth.noAccount')} <Link to="/register" style={{ color: theme.gold, textDecoration: 'none' }}>{t('auth.createOne')}</Link>
    </p>
  </AuthLayout>;
}

// --- Register ---
export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '', full_name: '', phone: '' });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.full_name, form.phone, {
        accept_terms: accepted,
        confirm_age: accepted,
      });
      toast.success(t('auth.accountCreated'));
      navigate('/browse');
    } catch (err) {
      toast.error(errorText(err, t('auth.registrationFailed')));
    } finally {
      setLoading(false);
    }
  };

  return <AuthLayout title={t('auth.joinGlamGo')} subtitle={t('auth.registerSubtitle')}>
    <form onSubmit={handleSubmit}>
      <Input label={t('auth.fullName')} value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required />
      <Input label={t('auth.email')} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
      <Input label={t('auth.phone')} type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
      {/* The backend requires 8. With no minLength here a 6-character password
          was accepted by the form, rejected by the server, and reported as an
          unexplained "Registration failed". */}
      <Input label={t('auth.password')} type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={8} hint={t('auth.passwordHint')} />
      {/* Nothing recorded that any customer had accepted the Terms, and Terms §2
          requires them to be 18 or over — neither was ever asked. One control
          covering both, because they are asserted in the same breath and a
          second checkbox for the age would only be clicked without reading. */}
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '4px 0 20px', fontSize: 12.5, color: theme.grey550, lineHeight: 1.6, cursor: 'pointer' }}>
        <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} required style={{ marginTop: 3 }} />
        <span>
          <Trans i18nKey="auth.acceptTerms">
            I am 18 or over and I accept the <Link to="/terms" target="_blank" style={{ color: theme.gold }}>Terms of Service</Link> and the <Link to="/privacy" target="_blank" style={{ color: theme.gold }}>Privacy Policy</Link>.
          </Trans>
          {/* A declaration with no stated consequence is a box people tick
              without reading. This says what happens if it was untrue. */}
          <span style={{ display: 'block', marginTop: 6, fontSize: 11, color: theme.grey400, lineHeight: 1.6 }}>
            {t('auth.ageNote')}
          </span>
        </span>
      </label>
      <SubmitBtn loading={loading} loadingLabel={t('auth.loading')} disabled={!accepted}>{t('auth.createAccount')}</SubmitBtn>
    </form>
    <p style={{ textAlign: 'center', color: theme.grey400, fontSize: 14, marginTop: 24 }}>
      {t('auth.alreadyAccount')} <Link to="/login" style={{ color: theme.gold, textDecoration: 'none' }}>{t('auth.signInLink')}</Link>
    </p>
  </AuthLayout>;
}

function AuthLayout({ title, subtitle, children }) {
  return (
    <AuthShell>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 32 }}>
            <Sparkles size={18} color={theme.gold} />
            <span style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink, fontWeight: 600 }}>GLAM<span style={{ color: theme.gold }}>GO</span></span>
          </Link>
          <h1 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, margin: '0 0 8px', fontWeight: 400 }}>{title}</h1>
          <p style={{ color: theme.grey400, fontSize: 14, margin: 0 }}>{subtitle}</p>
        </div>
        {children}
    </AuthShell>
  );
}

function Input({ label, type = 'text', value, onChange, required, minLength, hint }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {/* CSS uppercase, not label.toUpperCase(): JS keeps the Greek tonos
          ('κωδικός' → 'ΚΩΔΙΚΌΣ'), which is misspelt in all-caps Greek. */}
      <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8, textTransform: 'uppercase' }}>{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} minLength={minLength} style={{
        width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4,
        fontSize: 15, color: theme.ink, background: theme.cream, outline: 'none',
        boxSizing: 'border-box', fontFamily: theme.font.body,
        transition: 'border-color 0.2s'
      }}
        onFocus={e => e.target.style.borderColor = theme.gold}
        onBlur={e => e.target.style.borderColor = theme.sand}
      />
      {hint && <p style={{ margin: '6px 0 0', fontSize: 11, color: theme.grey400 }}>{hint}</p>}
    </div>
  );
}

function SubmitBtn({ children, loading, loadingLabel, disabled }) {
  const off = loading || disabled;
  return (
    <button type="submit" disabled={off} style={{
      width: '100%', padding: '16px', background: theme.ink, color: theme.white,
      border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.15em',
      cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.7 : 1,
      marginTop: 8, fontFamily: theme.font.body, fontWeight: 500
    }}>
      {loading ? loadingLabel : children}
    </button>
  );
}
